import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { PlatformService } from '@core/services/platform.service';
import { DestroyableComponent } from '@models/destroyable.component';
import {
  Collection,
  CollectionTypeEnum,
  EContentPanelRoute,
  ImageSizeEnum,
  Language,
  otherSubjectId,
  PublishStatusEnum,
  Subject,
  UserService,
  UserServiceCategory,
} from 'lingo2-models';
import { tap, zip } from 'rxjs';
import { LanguageService } from '../language.service';
import { FilesService } from '../lingo2-files/files.service';
import { DictionaryService, LanguageFromDictionary, SubjectFromDictionary } from './dictionary.service';
import { TeachingSubject, UserCollectionOfLessons, UserServiceBff } from './user-profile.service';

export function subjectPseudoId(subject: TeachingSubject): string {
  return subject.subjectOtherId ? String(subject.subjectOtherId) : subject.subjectId.toString();
}

export function findSubjectById(subjects: Subject[], teaching: TeachingSubject): any {
  if (teaching?.subjectId === otherSubjectId) {
    return {
      id: +teaching.subjectOtherId,
      code: 'other...',
      title: teaching.subjectOtherName,
      language_id: 0,
    };
  }
  return subjects.find((_s) => +_s.id === +teaching?.subjectId);
}

@Injectable({
  providedIn: 'root',
})
export class HelpersBffService extends DestroyableComponent {
  protected usCategories: UserServiceCategory[] = [];
  protected languages: LanguageFromDictionary[] = [];
  protected subjects: SubjectFromDictionary[] = [];
  protected currentLanguage: Language;

  constructor(
    protected http: HttpClient,
    protected dictionaryService: DictionaryService,
    protected languageService: LanguageService,
    protected readonly platform: PlatformService,
  ) {
    super(platform);

    zip([
      this.dictionaryService.uscategories,
      this.dictionaryService.languages,
      this.dictionaryService.subjec2ts,
      this.languageService.language$,
    ])
      .pipe(
        tap(([categories, languages, subjects, currentLanguage]) => {
          this.usCategories = categories;
          this.languages = languages;
          this.subjects = subjects;
          this.currentLanguage = currentLanguage;
        }),
      )
      .subscribe();
  }

  public convertBffSubjectsToLegacy(subjects: SubjectFromDictionary[]): Subject[] {
    return subjects.map(
      (subject) =>
        new Subject({
          id: subject._id,
          code: subject.code,
          title: subject.title,
          category_id: subject.categoryId,
          language_id: subject.languageId,
          levels_category_id: subject.levelCategoryId,
          cover_id: subject.coverId,
        }),
    );
  }

  public convertBffLanguagesToLegacy(languages: LanguageFromDictionary[]): Language[] {
    return languages.map(
      (language) =>
        new Language({
          id: language._id,
          code: language.code,
          title: language.titleEn,
          title_native: language.titleNative,
        }),
    );
  }

  public convertBffServicesToLegacy(services: UserServiceBff[]): UserService[] {
    return services.map(
      (service) =>
        new UserService({
          id: service._id,
          title: service.title,
          type: service.type,
          excerpt: service.excerpt,
          description: service.description,
          price_tier: service.priceTier,
          slug: service.slug,
          cover_id: service.coverId,
          author_id: service.authorId,
          school_id: service.schoolId,
          subject_id: service.subjectId,
          subject_other_name: null,
          subject_other_id: null,
          language_id: service.languageId,
          language: {
            id: service.languageId,
            code: service.languageCode,
            title:
              this.currentLanguage.code === 'en'
                ? this.languages.find((lang) => lang._id === service.languageId).titleEn
                : this.languages.find((lang) => lang._id === service.languageId).titleRu,
            title_native: this.languages.find((lang) => lang._id === service.languageId).titleNative,
          },
          duration: service.duration,
          discount: service.discount,
          category_id: service.categoryId,
          category: {
            id: service.categoryId,
            code: this.usCategories.find((cat) => cat.id === service.categoryId).code,
            title: this.usCategories.find((cat) => cat.id === service.categoryId).title,
            types: this.usCategories.find((cat) => cat.id === service.categoryId).types,
          },
          visit_info: {
            is_favorite: service.visitInfo['isBookmarked'],
          },
          moderation_status: service.moderationStatus,
          moderation_message: service.moderationMessage,
          created_at: (service.createdAt as any) || Date.now(),
          changed_at: (service.createdAt as any) || Date.now(),
          published_at: (service.publishedAt as any) || Date.now(),
          purl: service.purl,
          participants_limit: service.participantsLimit,
          options: {
            requirements: service.requirements,
            skills: service.skills,
            weekdays: service.weekdays,
            meetings_limit: service.meetingsLimit,
            age_from: service.ageFrom,
            age_to: service.ageTo,
          },
          rating: service.rating,
          can: {
            edit: service.visitInfo.canEdit,
            signup: service.canSignUp,
            schedule: false,
            archive: false,
            restore: false,
          },
          is: {
            on_moderation: service.isOnModeration,
            declined: service.isDeclined,
            individual: false,
            group: false,
            onetime: false,
            ongoing: false,
            series: false,
            draft: false,
            archived: false,
            approved: false,
            schedulable: false,
          },
        }),
    );
  }

  public convertBffCollectionsToLegacy(collections: UserCollectionOfLessons[]): Collection[] {
    return collections.map(
      (collection) =>
        new Collection({
          id: collection._id,
          title: collection.title,
          excerpt: collection.excerpt,
          description: collection.description,
          price_tiers: collection.priceTiers,
          slug: collection.slug,
          type: collection.type,
          cover_id: collection.multiCoverId[0],
          multi_cover_id: collection.multiCoverId,
          author_id: collection.authorId,
          subject_id: collection.subjectId,
          subject: {
            id: collection.subjectId,
            code: this.subjects.find((sbjct) => sbjct._id === collection.subjectId).code,
            title: this.subjects.find((sbjct) => sbjct._id === collection.subjectId).title,
            category_id: this.subjects.find((sbjct) => sbjct._id === collection.subjectId).categoryId,
            language_id: this.subjects.find((sbjct) => sbjct._id === collection.subjectId).languageId,
            levels_category_id: this.subjects.find((sbjct) => sbjct._id === collection.subjectId).levelCategoryId,
          },
          discount: collection.discount,
          keywords: [collection.excerpt],
          items: collection?.items.map((item) => ({
            id: item.lessonId,
            collection_id: item.collectionId,
            content_id: item.contentId,
            sort_order: item.sortOrder,
            is_open: item.isOpen,
          })),
          stats: {
            total: collection.numberOfTotal,
            // views_count: number;
            // reviews_count: number;
            // rating: number;
            // likes_count: number;
            // dislikes_count: number;
            // shares_count: number;
          },
          // options: {
          //   open_lessons: collection.;
          // };
          publish_status: PublishStatusEnum.published,
          changed_at: (collection.changedAt as any) || Date.now(),
          moderation_status: collection.moderationStatus,
          moderation_message: collection.moderationMessage,
          // visit_info: {
          //   licensed: collection.visitInfo.
          // }
          // promotion: collection;
          // promotion_status: PromotionStatusEnum;
          // advertisement: collection;
          // advertisement_status: AdvertisementStatusEnum;
          // access: CollectionAccessEnum;
          purl: collection.purl,
          // created_at: collection;
          // updated_at: Date;
          // hidden?: boolean;
          // valid?: boolean;
          // readonly?: boolean;
          // can: {
          //     edit = "edit",
          //     buy = "buy",
          //     upgrade = "upgrade",
          //     review = "review",
          //     publish = "publish",
          //     unpublish = "unpublish",
          //     archive = "archive",
          //     restore = "restore"
          // };
          // is: {
          //   draft = "draft",
          //   archived = "archived",
          //   on_moderation = "on_moderation",
          //   approved = "approved",
          //   declined = "declined"
          // };
        }),
    );
  }

  /** Полная ссылка на страницу индивидуального занятия */
  public userServiceRouteUniversal(item: UserService): string {
    if (item) {
      const subjectCode = item?.subject?.code || '-';
      const typeCode = this.usCategories.find((cat) => cat.id === item.category_id).code || '-';
      const title = (item.title || '')
        .replace(/[\\\/:;{}\[\]()_,-.!?'"`+#&=]/g, '')
        .trim()
        .replace(/\s+/g, '-');
      return [
        '/' + subjectCode,
        EContentPanelRoute.classes,
        typeCode,
        (title.length ? title + '-' : '') + item.slug,
      ].join('/');
    } else {
      return null;
    }
  }
}
